/**
 * API Utility with Automatic Authentication
 * Provides axios instance with automatic token injection, retry logic, and error handling
 */

import axios from 'axios';
import { API_CONFIG } from '../config/api';

const BACKEND_URL = API_CONFIG.BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/**
 * Delay utility for retry logic
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Create authenticated axios instance
 * Automatically adds Authorization header from localStorage
 */
export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 30 second timeout
  withCredentials: true,
});

// Module-level impersonation state.
// Initialized from sessionStorage to survive page reloads.
let _viewAsOrgId = (() => {
  try { return sessionStorage.getItem('viewAsOrgId') || null; }
  catch { return null; }
})();

/**
 * Set the organization ID to impersonate. Pass null to clear.
 * @param {string|null} orgId
 */
export const setViewAsOrgId = (orgId) => {
  _viewAsOrgId = orgId;
};

/**
 * Get the current impersonation org ID (or null).
 * @returns {string|null}
 */
export const getViewAsOrgId = () => _viewAsOrgId;

// Request interceptor to add auth token and impersonation header
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (_viewAsOrgId) {
      config.headers['X-View-As-Org'] = _viewAsOrgId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Handle 401 Unauthorized — dispatch event for AuthContext to handle.
    // Debounced to prevent multiple rapid redirects from background polls.
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!api._authExpiredFired) {
        api._authExpiredFired = true;
        window.dispatchEvent(new CustomEvent('auth:expired'));
        // Reset flag after a short window so future sessions can trigger again
        setTimeout(() => { api._authExpiredFired = false; }, 2000);
      }
      return Promise.reject(error);
    }
    
    // Retry logic for transient errors (idempotent methods only)
    const status = error.response?.status;
    const method = (config.method || 'get').toLowerCase();
    const isIdempotent = ['get', 'head', 'options', 'put'].includes(method);
    const isRetryable = RETRYABLE_STATUS_CODES.includes(status) || !error.response;
    const retryCount = config.__retryCount || 0;
    
    if (isRetryable && isIdempotent && retryCount < MAX_RETRIES && !config.__noRetry) {
      config.__retryCount = retryCount + 1;
      const backoffDelay = RETRY_DELAY * Math.pow(2, retryCount); // Exponential backoff
      console.warn(`API request failed, retrying (${config.__retryCount}/${MAX_RETRIES}) in ${backoffDelay}ms...`);
      await delay(backoffDelay);
      return apiClient(config);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Parse API error into user-friendly message
 * @param {Error} error - Axios error object
 * @returns {string} User-friendly error message
 */
export const parseApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail) {
    // FastAPI HTTPException detail may be a plain string or a structured
    // dict (e.g. fork-quota errors carry {message, existing_forks, limit}).
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object' && detail.message) return detail.message;
    return JSON.stringify(detail);
  }
  // Project-wide error envelope: { success: false, error: "...", detail: null }.
  // The message lives in `error`, not `detail`.
  const envelopeError = error.response?.data?.error;
  if (envelopeError && typeof envelopeError === 'string') {
    return envelopeError;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message === 'Network Error') {
    return 'Unable to connect to server. Please check your internet connection.';
  }
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }
  return error.message || 'An unexpected error occurred';
};

/**
 * Helper function to get auth headers manually
 * Use this if you need to pass headers to regular axios
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * API methods with automatic auth
 */
export const api = {
  /**
   * GET request with automatic auth
   * @param {string} url - API endpoint
   * @param {object} config - Optional axios config
   * @param {boolean} config.noRetry - Set true to disable retry logic
   */
  get: (url, config = {}) => {
    if (config.noRetry) {
      config.__noRetry = true;
      delete config.noRetry;
    }
    return apiClient.get(url, config);
  },
  
  /**
   * POST request with automatic auth
   * @param {string} url - API endpoint
   * @param {any} data - Request body
   * @param {object} config - Optional axios config
   */
  post: (url, data, config = {}) => {
    return apiClient.post(url, data, config);
  },
  
  /**
   * PUT request with automatic auth
   * @param {string} url - API endpoint
   * @param {any} data - Request body
   * @param {object} config - Optional axios config
   */
  put: (url, data, config = {}) => {
    return apiClient.put(url, data, config);
  },
  
  /**
   * DELETE request with automatic auth
   * @param {string} url - API endpoint
   * @param {object} config - Optional axios config
   */
  delete: (url, config = {}) => {
    return apiClient.delete(url, config);
  },
  
  /**
   * PATCH request with automatic auth
   * @param {string} url - API endpoint
   * @param {any} data - Request body
   * @param {object} config - Optional axios config
   */
  patch: (url, data, config = {}) => {
    return apiClient.patch(url, data, config);
  },
};

export default api;
